import { ReactNode } from "react";

import { css, keyframes } from "@compiled/react";
import { ReactComponentLike } from "prop-types";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives/responsive";
import UFOSegment from "@atlaskit/react-ufo/segment";

import {
    Adg4DraggableTab,
    Adg4DraggableTabList,
    Adg4TabPanel,
    Adg4Tabs
} from "@/components/adg4/tabs/src";
import { IconTab } from "@/components/confluence/product-tour/src/icon-tab";
import { useUserContext } from "@/components/user-context";

import { DatabasesIcon } from "../icons/databases-icon";
import { PagesIcon } from "../icons/pages-icon";
import { SpacesIcon } from "../icons/spaces-icon";
import { WhiteboardsIcon } from "../icons/whiteboards-icon";

import { DatabasesContentType } from "./content-types/databases-content-type";
import { PagesContentType } from "./content-types/pages-content-type";
import { SpacesContentType } from "./content-types/spaces-content-type";
import { WhiteboardsContentType } from "./content-types/whiteboards-content-type";
import {
    tabbedHeroSectionDatabasesMessages,
    tabbedHeroSectionPagesMessages,
    tabbedHeroSectionSpacesMessages,
    tabbedHeroSectionWhiteboardsMessages
} from "./messages/tabbed-hero-section.messages";

const tabStyles = css({
    maxWidth: 1114,
    marginInline: "auto"
});

const iconTabListStyles = css({
    paddingBlockStart: 8,
    margin: 0,
    width: "100%",
    justifyContent: "space-between",
    [media.above.md]: {
        paddingInline: 24
    },
    [media.below.md]: {
        paddingInline: 16,
        gap: 16
    }
});

const iconTabStyles = css({
    borderRadius: 2
});

const opacityIn = keyframes({
    "0%": {
        opacity: 0
    },
    "100%": {
        opacity: 1
    }
});

const contentContainer = css({
    display: "flex",
    flexDirection: "column",
    marginInline: "auto",
    paddingBlockStart: 32,
    minHeight: 460,
    [media.above.md]: {
        paddingInline: 24
    },
    [media.below.md]: {
        justifyContent: "end",
        paddingInline: 16,
        paddingBlockStart: 32
    },
    [media.only.sm]: {
        maxWidth: "75%"
    },

    animation: `${opacityIn} linear 500ms 1`
});

type ContentTypesSectionProps = {
    isConfluenceCrossflow?: boolean;
};

export const ContentTypesSection = ({
    isConfluenceCrossflow = false
}: ContentTypesSectionProps) => {
    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { user } = useUserContext();

    const tabList: {
        title: string;
        icon: ReactNode;
        content: ReactComponentLike;
        lozenge?: string;
        eventProps?: {
            label?: string;
            labelEnglish?: string;
        };
    }[] = [
        {
            title: intl.formatMessage(tabbedHeroSectionPagesMessages.tabName),
            icon: <PagesIcon width="1.5em" />,
            content: PagesContentType,
            eventProps: {
                label: "content-type-tab-pages",
                labelEnglish: "Pages"
            }
        },
        {
            title: intl.formatMessage(
                tabbedHeroSectionWhiteboardsMessages.tabName
            ),
            icon: <WhiteboardsIcon width="1.5em" />,
            content: WhiteboardsContentType,
            eventProps: {
                label: "content-type-tab-whiteboard",
                labelEnglish: "Whiteboards"
            }
        },
        {
            title: intl.formatMessage(
                tabbedHeroSectionDatabasesMessages.tabName
            ),
            icon: <DatabasesIcon width="1.5em" />,
            content: DatabasesContentType,
            eventProps: {
                label: "content-type-tab-databases",
                labelEnglish: "Databases"
            }
        },
        {
            title: intl.formatMessage(tabbedHeroSectionSpacesMessages.tabName),
            icon: <SpacesIcon width="1.5em" />,
            content: SpacesContentType,
            eventProps: {
                label: "content-type-tab-spaces",
                labelEnglish: "Spaces"
            }
        }
    ];

    return (
        <UFOSegment name="confluence-pt-cfind-1234-contentTypesSection">
            <div css={tabStyles}>
                <Adg4Tabs>
                    <Adg4DraggableTabList css={iconTabListStyles}>
                        {tabList.map((tab, index) => (
                            <Adg4DraggableTab
                                key={index}
                                css={iconTabStyles}
                                dataEventProps={
                                    !isConfluenceCrossflow
                                        ? {
                                              "data-product-key": "confluence",
                                              "data-event": "clicked",
                                              "data-event-component": "tab",
                                              "data-event-container":
                                                  "contentTypesSection",
                                              "data-label":
                                                  tab.eventProps?.label ??
                                                  "content-type-tab",
                                              "data-label-english":
                                                  tab.eventProps?.labelEnglish,
                                              "data-track-event":
                                                  "confluence-pt.content-types.tab"
                                          }
                                        : undefined
                                }
                                onClick={() => {
                                    if (!isConfluenceCrossflow) {
                                        return;
                                    }

                                    createAnalyticsEvent({
                                        eventName:
                                            "confluence-crossflow-content-types-tab-clicked",
                                        eventType: "ui",
                                        action: "clicked",
                                        actionSubject: "confluenceCrossflowTab",
                                        attributes: {
                                            reactComponent:
                                                "ContentTypesSection",
                                            tab: tab.eventProps?.labelEnglish?.toLowerCase(),
                                            userTeamType:
                                                user.extended_profile?.team_type
                                        }
                                    }).fire();
                                }}
                            >
                                <IconTab
                                    icon={tab.icon}
                                    title={tab.title}
                                    lozenge={tab.lozenge}
                                />
                            </Adg4DraggableTab>
                        ))}
                    </Adg4DraggableTabList>
                    {tabList.map((tab, index) => (
                        <Adg4TabPanel key={index}>
                            <div css={contentContainer}>
                                {
                                    <tab.content
                                        index={index}
                                        isConfluenceCrossflow={
                                            isConfluenceCrossflow
                                        }
                                    />
                                }
                            </div>
                        </Adg4TabPanel>
                    ))}
                </Adg4Tabs>
            </div>
        </UFOSegment>
    );
};

ContentTypesSection.displayName = "ContentTypesSection";

export default ContentTypesSection;
